/**
 * @fileoverview Users API Client (Contract-First)
 * @module @monorepo/api-client/clients
 *
 * 100% type-safe Users API client generated from contract.
 * All methods, types, and validation come from USERS_API_CONTRACT.
 */

import {
	USERS_API_CONTRACT,
	type CreateUserPayload,
	type UpdateUserPayload,
	type ListUsersQuery,
	DeleteUserApiResponse,
} from '@monorepo/types';
import { ContractClient } from '../core/contract-client';
import type { HttpClient } from '../core/http-client';

/**
 * Users API Client
 *
 * Contract-first client for user management operations.
 * All methods are type-safe and validated against the API contract.
 *
 * @example
 * ```typescript
 * const usersClient = new UsersClient(httpClient);
 *
 * // Create user
 * const user = await usersClient.create({
 *   name: 'John Doe',
 *   email: 'john@example.com',
 *   role: 'user',
 * });
 *
 * // List users with pagination
 * const result = await usersClient.list({ page: 1, limit: 10 });
 *
 * // Get user by ID
 * const user = await usersClient.getOne('user-123');
 *
 * // Update user
 * const updated = await usersClient.update('user-123', { name: 'Jane Doe' });
 *
 * // Delete user
 * await usersClient.delete('user-123');
 * ```
 */
export class UsersClient extends ContractClient<typeof USERS_API_CONTRACT> {
	constructor(httpClient: HttpClient) {
		super(httpClient, USERS_API_CONTRACT, {
			validateResponses: true, // ✅ ENABLED: Validate responses against contract
			validateRequests: true, // ✅ ENABLED: Validate requests before sending
			throwOnValidationError: true, // Throw detailed errors on validation failure
		});
	}

	/**
	 * Create a new user
	 * POST /users
	 * @returns Promise<UserApiResponse> - Wrapped response: { success, data: User, timestamp }
	 */
	async create(payload: CreateUserPayload) {
		return this.execute('create', { body: payload });
	}

	/**
	 * List all users with pagination
	 * GET /users
	 * @returns Promise<PaginatedUsersApiResponse> - Wrapped response: { success, data: User[], meta, timestamp }
	 */
	async list(query?: ListUsersQuery) {
		return this.execute('list', { query });
	}

	/**
	 * Get a single user by ID
	 * GET /users/:id
	 * @returns Promise<UserApiResponse> - Wrapped response: { success, data: User, timestamp }
	 */
	async getOne(id: string) {
		return this.execute('getOne', { params: { id } });
	}

	/**
	 * Update a user
	 * PATCH /users/:id
	 * @returns Promise<UserApiResponse> - Wrapped response: { success, data: User, timestamp }
	 */
	async update(id: string, payload: UpdateUserPayload) {
		return this.execute('update', { params: { id }, body: payload });
	}

	/**
	 * Delete a user
	 * DELETE /users/:id
	 * @returns Promise<DeleteUserApiResponse> - Wrapped response: { success, data: { message }, timestamp }
	 */
	async delete(id: string): Promise<DeleteUserApiResponse>  {
		return this.execute('delete', { params: { id } });
	}
}
